import { useEffect, useState } from 'react'
import { CalendarClock, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { t } from '@/i18n'

/** How close to the end the shop starts being told. */
const WARN_WITHIN_DAYS = 5

/**
 * Whole days from now until the license runs out.
 *
 * Counted on calendar days rather than 24-hour blocks, because "2 days left" has to mean the
 * same thing to the owner as it does to a wall calendar. A license ending tomorrow morning is
 * one day left, not zero because it is less than 24 hours away.
 */
export function daysUntil(expiry: string | null): number | null {
  if (!expiry) return null

  const end = new Date(expiry)
  if (Number.isNaN(end.getTime())) return null

  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)

  const startOfEnd = new Date(end)
  startOfEnd.setHours(0, 0, 0, 0)

  return Math.round((startOfEnd.getTime() - startOfToday.getTime()) / 86_400_000)
}

/** The license date, for the foot of the navigation. */
export function LicenseLine() {
  const session = useSession((state) => state.session)
  const days = daysUntil(session?.licenseExpiresAtUtc ?? null)

  if (!session?.licenseExpiresAtUtc) {
    return null
  }

  const on = new Date(session.licenseExpiresAtUtc).toLocaleDateString()
  const expired = days !== null && days < 0
  const soon = days !== null && days >= 0 && days <= WARN_WITHIN_DAYS

  return (
    <div
      className={`mt-4 rounded-lg border px-3 py-2 text-xs ${
        expired || soon ? 'border-warn-500/40 bg-warn-500/10' : 'surface-sunken border-transparent'
      }`}
    >
      <p className="flex items-center gap-1.5 font-medium">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
        {t('License')}
      </p>
      <p className="mt-0.5 text-[var(--text-secondary)]">
        {expired ? t('Expired') : t('Valid until')} <span className="tabular">{on}</span>
      </p>
      {soon && (
        <p className="mt-0.5 font-medium text-warn-500">
          {days === 0
            ? t('Last day')
            : days === 1
              ? t('1 day left')
              : t('{days} days left', { days })}
        </p>
      )}
    </div>
  )
}

/**
 * Says the license is nearly up, once a day, in the last few days.
 *
 * <p>
 * Once a day is the whole of the design. Shown once ever, it lands on whoever happens to open the
 * app that morning and is never seen again by the person who actually pays the bill; shown on
 * every navigation, it becomes something everybody clicks away without reading by the second
 * afternoon. A cashier seeing it is not a mistake either — they are the ones who tell the owner.
 * </p>
 */
export function LicenseWarning() {
  const session = useSession((state) => state.session)
  const [isOpen, setOpen] = useState(false)

  const expiry = session?.licenseExpiresAtUtc ?? null
  const days = daysUntil(expiry)
  const storeCode = session?.storeCode
  const key = storeCode ? `octapos.license-notice.${storeCode}` : null

  useEffect(() => {
    if (days === null || days > WARN_WITHIN_DAYS || !key) {
      return
    }

    // Keyed on the day so it returns tomorrow, and on the store so signing into a second shop
    // on the same device does not silence its own warning.
    const today = new Date().toDateString()
    if (localStorage.getItem(key) === today) {
      return
    }

    localStorage.setItem(key, today)
    setOpen(true)
  }, [days, key])

  if (days === null) {
    return null
  }

  const expired = days < 0
  const on = expiry ? new Date(expiry).toLocaleDateString() : ''

  return (
    <Modal
      open={isOpen}
      onClose={() => setOpen(false)}
      title={expired ? t('Your license has ended') : t('Your license is ending soon')}
      footer={
        <Button onClick={() => setOpen(false)}>
          {expired ? t('I understand') : t('Thanks, I will sort it')}
        </Button>
      }
    >
      <div className="flex gap-3">
        <div className="surface-sunken flex h-11 w-11 shrink-0 items-center justify-center rounded-xl">
          <CalendarClock className="h-5 w-5 text-warn-500" />
        </div>

        <div className="min-w-0 text-sm">
          {expired ? (
            <p>
              {t('{store} license ended on {date}. Get in touch to renew and keep everything working exactly as it is.', {
                store: session?.storeName ?? '',
                date: on,
              })}
            </p>
          ) : (
            <p>
              {t('{store} license ends on {date} — {when}. Renewing before then keeps the till, your reports and your history exactly where they are, with nothing to set up again.', {
                store: session?.storeName ?? '',
                date: on,
                when:
                  days === 0
                    ? t('today is the last day')
                    : days === 1
                      ? t('1 day from now')
                      : t('{days} days from now', { days }),
              })}
            </p>
          )}

          <p className="mt-2 text-[var(--text-secondary)]">
            {t('Nothing has changed yet, and you can carry on selling. This is just so it does not catch you on a busy morning.')}
          </p>
        </div>
      </div>
    </Modal>
  )
}
